import { useState } from "react";
import { Reveal, MaskLineInView } from "./Reveal";
import { IMAGES, api } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, HandCoins, ScanLine } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import CashAppLine, { CASHAPP_URL } from "./CashAppLine";

const amounts = [25, 50, 100, 250];

export default function Give() {
  const [f, setF] = useState({ name: "", email: "", amount: 50, fund: "General Offering", frequency: "one-time" });
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const amount = custom ? parseFloat(custom) : f.amount;
    if (!f.name || !f.email || !amount) { toast.error("Please add your name, email and an amount."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/donations", { ...f, amount });
      toast.success(data.message);
      setF({ name: "", email: "", amount: 50, fund: "General Offering", frequency: "one-time" });
      setCustom("");
    } catch { toast.error("Something went wrong. Please call 726-247-5926."); }
    setBusy(false);
  };

  return (
    <section id="give" className="relative py-28 md:py-40 overflow-hidden bg-[#05030A]">
      <div className="absolute inset-0">
        <img src={IMAGES.fountain} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05030A] via-[#05030A]/80 to-[#05030A]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal><span className="section-label">05 — Sow Into the Mission</span></Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] mt-5 text-white">
            <MaskLineInView>Let the river</MaskLineInView>
            <MaskLineInView delay={0.1}><span className="italic text-gold">keep flowing.</span></MaskLineInView>
          </h2>
          <Reveal delay={0.2}>
            <p className="text-lg text-[#B4ADC1] font-light mt-6 leading-relaxed">
              Your giving fuels food support, family prayer, youth mentorship, senior care and the Living Water digital mission across San Antonio and beyond.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-8 flex flex-col gap-5 items-start">
              <CashAppLine testId="give-cashapp-btn" />
              <div className="glass rounded-2xl p-4 flex items-center gap-4 self-start" data-testid="give-cashapp-qr">
                <div className="bg-white rounded-xl p-2.5">
                  <QRCodeSVG value={CASHAPP_URL} size={104} bgColor="#ffffff" fgColor="#05030A" level="M" />
                </div>
                <div className="min-w-[150px]">
                  <p className="text-sm text-white flex items-center gap-2 whitespace-nowrap"><ScanLine className="w-4 h-4 text-[#00D632]" /> Scan to give</p>
                  <p className="text-xs text-[#7A708A] font-mono mt-1 leading-relaxed">Point your phone camera at the code — one tap.</p>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="text-xs text-[#7A708A] font-mono mt-8 leading-relaxed border-l-2 border-gold/40 pl-4">
              For your safety: FOLWAC leaders will NEVER privately demand gift cards, cryptocurrency or emergency transfers. Verified line: 726-247-5926.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="glass rounded-3xl p-8" data-testid="give-form">
            <div className="grid grid-cols-4 gap-2">
              {amounts.map((a) => (
                <button type="button" key={a} onClick={() => { set("amount", a); setCustom(""); }} className={`py-3 rounded-xl text-sm font-medium transition-colors ${!custom && f.amount === a ? "bg-gold text-black" : "bg-white/5 text-[#B4ADC1] hover:bg-white/10"}`} data-testid={`give-amount-${a}`}>
                  ${a}
                </button>
              ))}
            </div>
            <Input className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-[#7A708A] outline-none focus:border-gold/50 transition-colors mt-3" placeholder="Custom amount ($)" value={custom} onChange={(e) => setCustom(e.target.value.replace(/[^0-9.]/g, ""))} data-testid="give-custom" />

            <div className="flex bg-white/5 border border-white/10 rounded-xl p-1 mt-4">
              {["one-time", "monthly"].map((opt) => (
                <button type="button" key={opt} onClick={() => set("frequency", opt)} className={`flex-1 rounded-lg py-2.5 text-sm capitalize transition-colors ${f.frequency === opt ? "bg-gold text-black" : "text-[#B4ADC1]"}`} data-testid={`give-freq-${opt}`}>
                  {opt}
                </button>
              ))}
            </div>

            <div className="grid gap-3 mt-4">
              <Input className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-[#7A708A] outline-none focus:border-gold/50 transition-colors" placeholder="Full name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="give-name" />
              <Input className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-[#7A708A] outline-none focus:border-gold/50 transition-colors" placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="give-email" />
            </div>

            <button type="submit" disabled={busy} className="w-full py-4 rounded-full bg-gold text-black font-medium hover:bg-[#e6c14e] transition-colors flex items-center justify-center gap-2 mt-5 disabled:opacity-50" data-testid="give-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandCoins className="w-4 h-4" />} Give ${custom || f.amount} {f.frequency === "monthly" ? "/ month" : ""}
            </button>
            <p className="text-[0.6rem] text-[#7A708A] mt-3 text-center font-mono">Records your giving intent · Church will follow up with secure giving details</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
